import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, Loader2, X, FileText, ReceiptText, Share2, Car, History } from 'lucide-react';
import { CustomerHistoryPanel } from '../../../components/admin/billing/CustomerHistoryPanel';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, sharePdfToWhatsApp, renderMessage } from '../../../lib/waShare';
import { fmtINR, formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import VehicleEditor from '../../../components/admin/billing/VehicleEditor';
import VehicleHistoryModal from '../../../components/admin/billing/VehicleHistoryModal';

const PREFIXES = ['', 'Mr.', 'Mrs.', 'Miss.', 'Ms.', 'Dr.', 'Prof.', 'M/s.'];

const empty = () => ({ prefix: '', name: '', phone: '', email: '', address: '', gstin: '', state: '', vehicles: [] });

const Modal = ({ title, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div
      className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="modal-close">
          <X size={18} />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const Customers = () => {
  const { authHeader, user } = useAuth();
  // The dues statement is served by the reports module, so only show the
  // button to someone who can actually fetch it.
  const canSeeDues = !!(user?.is_master_admin || (user?.permissions || {}).can_view_reports);
  const [historyFor, setHistoryFor] = useState(null);
  const [list, setList] = useState([]);
  const [duesBusy, setDuesBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Printable "what you owe us" statement — every pending invoice, all time.
  const duesStatement = async (c) => {
    setDuesBusy(c.id); setErr('');
    try {
      const res = await axios.get(`${API}/reports/receivables/${c.id}/statement`,
        { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pending_Payments_${(c.name || 'customer').replace(/\s+/g, '_')}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr(formatErr(e)); }
    finally { setDuesBusy(''); }
  };
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  // Vehicle service history modal state
  const [historyTarget, setHistoryTarget] = useState(null); // { customer, vehicle }
  // Statement modal state
  const [stmtCustomer, setStmtCustomer] = useState(null);
  const [stmtSharing, setStmtSharing] = useState(false);
  const [stmtRange, setStmtRange] = useState(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    return { date_from: first, date_to: now.toISOString().slice(0, 10) };
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/billing/customers`, {
        headers: authHeader(), params: q ? { q } : {},
      });
      setList(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load the vehicle catalog once (lazy — only when the user opens the form
  // or first hits the page). Safe to retry on failure.
  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/vehicles/catalog`, { headers: authHeader() });
      setCatalog(data);
    } catch (_e) {
      // Catalog failure is non-fatal — user can still type free-text values.
      setCatalog({ cars: {}, motorcycles: {}, scooters: {} });
    }
  }, [authHeader]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const openNew = () => { setEditing('new'); setForm(empty()); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      prefix: c.prefix || '',
      name: c.name || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      gstin: c.gstin || '',
      state: c.state || '',
      vehicles: Array.isArray(c.vehicles) ? c.vehicles : [],
    });
  };
  const close = () => { setEditing(null); setForm(empty()); };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/billing/customers`, form, { headers: authHeader() });
      } else {
        await axios.put(`${API}/billing/customers/${editing}`, form, { headers: authHeader() });
      }
      close();
      fetchAll();
    } catch (e) {
      notifyErr(e);
    } finally {
      setSaving(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/billing/customers/${c.id}`, { headers: authHeader() });
      fetchAll();
    } catch (e) {
      notifyErr(e);
    }
  };

  // Pre-fill a WhatsApp message with the customer's outstanding balance + a
  // direct link to the rendered statement PDF. Opens wa.me in a new tab so
  // the user finishes the send inside the WhatsApp app/web client.
  const shareStatementOnWhatsApp = async () => {
    if (!stmtCustomer) return;
    setStmtSharing(true);
    try {
      const { data } = await axios.get(
        `${API}/billing/customers/${stmtCustomer.id}/statement-summary`,
        { headers: authHeader(), params: { date_from: stmtRange.date_from, date_to: stmtRange.date_to } },
      );
      const url = `${window.location.origin}/api/billing/customers/${stmtCustomer.id}/statement` +
                  `?date_from=${stmtRange.date_from}&date_to=${stmtRange.date_to}`;
      const message = renderMessage('statement', {
        customer: greetName(stmtCustomer.prefix, stmtCustomer.name),
        date_from: stmtRange.date_from, date_to: stmtRange.date_to,
        outstanding: data.outstanding_total > 0 ? fmtINR(data.outstanding_total) : '',
        invoice_count: `${data.invoice_count} invoice${data.invoice_count === 1 ? '' : 's'}`,
        when_settled: data.outstanding_total <= 0 && data.invoice_count > 0 ? true : '',
        pdf_link: url,
      });
      await sharePdfToWhatsApp({
        getPdf: async () => (await axios.get(url, { responseType: 'blob', headers: authHeader() })).data,
        filename: `Statement_${String(stmtCustomer.name || 'customer').replace(/[^\w]+/g, '_')}_${stmtRange.date_to}.pdf`,
        title: 'Statement', message, phone: stmtCustomer.phone,
      });
    } catch (e) {
      notifyErr(e);
    } finally {
      setStmtSharing(false);
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">Customers</h1>
            <p className="text-sm text-slate-500">Manage customer profiles used in estimates &amp; invoices.</p>
          </div>
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
            data-testid="new-customer-btn"
          >
            <Plus size={16} /> New Customer
          </button>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone or email"
            className={`${inputCls} pl-9`}
            data-testid="customer-search"
          />
        </div>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">{err}</div>}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center text-slate-500 py-12"><Loader2 className="animate-spin inline mr-2" size={18} /> Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-center text-slate-500 py-12">No customers yet. Click <b>New Customer</b> to add one.</div>
          ) : (
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Vehicles</th>
                  <th className="text-right px-4 py-3 font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{[c.prefix, c.name].filter(Boolean).join(' ')}</td>
                    <td className="px-4 py-3 text-slate-600">{c.phone || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.email || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell" data-testid={`customer-vehicles-${c.id}`}>
                      {(c.vehicles && c.vehicles.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {c.vehicles.slice(0, 2).map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => setHistoryTarget({ customer: c, vehicle: v })}
                              className="inline-flex items-center gap-1 text-[11px] bg-slate-100 hover:bg-teal-50 hover:border-teal-300 border border-slate-200 rounded-full px-2 py-0.5 transition text-slate-700 hover:text-teal-700"
                              title="View service history for this vehicle"
                              data-testid={`vehicle-pill-${c.id}-${v.id}`}
                            >
                              <Car size={11} />
                              {[v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                              {v.registration_number ? ` · ${v.registration_number}` : ''}
                            </button>
                          ))}
                          {c.vehicles.length > 2 && (
                            <span className="text-[11px] text-slate-500">+{c.vehicles.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      {canSeeDues && (
                      <button onClick={() => duesStatement(c)} disabled={duesBusy === c.id} className="p-1.5 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-50" title="Pending payments statement (PDF)" data-testid={`dues-statement-${c.id}`}>
                        {duesBusy === c.id ? <Loader2 size={15} className="animate-spin" /> : <ReceiptText size={15} />}
                      </button>
                      )}
                      <button onClick={() => setStmtCustomer(c)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Monthly statement PDF" data-testid={`statement-customer-${c.id}`}>
                        <FileText size={15} />
                      </button>
                      <button onClick={() => setHistoryFor(c)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Booking history" data-testid={`history-customer-${c.id}`}>
                        <History size={15} />
                      </button>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Edit" data-testid={`edit-customer-${c.id}`}>
                        <Edit size={15} />
                      </button>
                      <button onClick={() => del(c)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Delete" data-testid={`delete-customer-${c.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {editing && (
          <Modal title={editing === 'new' ? 'New customer' : 'Edit customer'} onClose={close} wide>
            <div className="space-y-3">
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-4 sm:col-span-3">
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Prefix</label>
                  <select
                    value={form.prefix}
                    onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                    className={inputCls}
                    data-testid="customer-prefix-input"
                  >
                    {PREFIXES.map((p) => (
                      <option key={p || 'none'} value={p}>{p || '—'}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-8 sm:col-span-9">
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="customer-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="customer-phone-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="customer-email-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Address</label>
                <textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="customer-address-input" />
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                    GSTIN <span className="text-[10px] normal-case text-slate-400 font-normal tracking-normal">(optional · for B2B GST invoices)</span>
                  </label>
                  <input type="text" value={form.gstin || ''} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="15-character GSTIN" maxLength={15} data-testid="customer-gstin-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">State</label>
                  <input type="text" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="KA / MH / DL …" maxLength={2} data-testid="customer-state-input" />
                </div>
              </div>
              <div className="pt-2 border-t border-slate-200">
                <VehicleEditor
                  vehicles={form.vehicles || []}
                  onChange={(vehicles) => setForm({ ...form, vehicles })}
                  catalog={catalog}
                  onCatalogChange={fetchCatalog}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={close} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={save} disabled={!form.name.trim() || saving}
                  className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg disabled:opacity-50"
                  data-testid="customer-save-btn">
                  {saving ? <Loader2 className="animate-spin inline" size={14} /> : 'Save'}
                </button>
              </div>
            </div>
          </Modal>
        )}
        {stmtCustomer && (
          <Modal title={`Statement — ${[stmtCustomer.prefix, stmtCustomer.name].filter(Boolean).join(' ')}`} onClose={() => setStmtCustomer(null)}>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Generate a one-page statement listing every invoice for this customer in the selected date range.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">From</label>
                  <input type="date" value={stmtRange.date_from} onChange={(e) => setStmtRange({ ...stmtRange, date_from: e.target.value })} className={inputCls} data-testid="stmt-from" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">To</label>
                  <input type="date" value={stmtRange.date_to} onChange={(e) => setStmtRange({ ...stmtRange, date_to: e.target.value })} className={inputCls} data-testid="stmt-to" />
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                <button onClick={() => setStmtCustomer(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Close</button>
                <button
                  onClick={shareStatementOnWhatsApp}
                  disabled={stmtSharing}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-lg"
                  data-testid="stmt-whatsapp-btn"
                  title="Open WhatsApp with a pre-filled message"
                >
                  {stmtSharing ? <Loader2 className="animate-spin" size={14} /> : <Share2 size={14} />}
                  WhatsApp
                </button>
                <a
                  href={`${API}/billing/customers/${stmtCustomer.id}/statement?date_from=${stmtRange.date_from}&date_to=${stmtRange.date_to}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                  data-testid="stmt-open-btn"
                >
                  <FileText size={14} /> Open PDF
                </a>
              </div>
            </div>
          </Modal>
        )}
        {historyTarget && (
          <VehicleHistoryModal
            customer={historyTarget.customer}
            vehicle={historyTarget.vehicle}
            onClose={() => setHistoryTarget(null)}
          />
        )}
      </div>
      {historyFor && <CustomerHistoryPanel customer={historyFor} authHeader={authHeader} onClose={() => setHistoryFor(null)} />}
    </div>
  );
};

export default Customers;
